import { ArrowPathIcon, CheckBadgeIcon } from '@heroicons/react/24/outline'
import { BeatLoader } from 'react-spinners'
import { Desert } from '../../../svgs'
import { Button } from '../../../ui/new/button'
import PreviewRequestCard from './cards/PreviewRequestCard'
import { Checkbox } from '../../../ui/new/checkbox'
import { Textarea } from '../../../ui/new/textArea'
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../../ui/form'
import exchangeRequestService from '../../../../api/services/exchangeRequestService'
import { CreateRequestData } from '../../../../@types'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { z } from 'zod'
import { Dispatch, SetStateAction } from 'react'

type Props = {
  requests: Map<number, CreateRequestData>
  requestSubmitHandler: (message: string) => void
  submittingRequest: boolean
  sendUrgentMessage: boolean
  hasDuplicate: boolean
  setSendUrgentMessage: Dispatch<SetStateAction<boolean>>
}

export const ExchangeSubmissionConfirmation = ({
  requests,
  requestSubmitHandler,
  submittingRequest,
  sendUrgentMessage,
  hasDuplicate,
  setSendUrgentMessage,
}: Props) => {
  const schema = z.object({
    urgentMessage: sendUrgentMessage
      ? z
          .string()
          .min(1, {
            message: 'Tens de especificar um motivo!',
          })
          .max(2048)
      : z.string().optional(),
  })

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: z.infer<typeof schema>) => {
    await requestSubmitHandler(data.urgentMessage ?? '')
  }

  // const { conflictSeverity } = useContext(ConflictsContext);

  return (
    <>
      <div className="text-left w-full">
        {requests.size === 0 ? (
          <div className="flex flex-col justify-center items-center">
            <Desert className="w-full" />
            <p>Ainda não escolheste nenhuma turma em nenhuma das disciplinas selecionadas.</p>
          </div>
        ) : (
          <div>
            {Array.from(requests.values()).map((request) => (
              <PreviewRequestCard key={request.courseUnitId} request={request} />
            ))}
          </div>
        )}
      </div>

      {requests.size > 0 && (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-y-4 items-center mx-auto w-full">
            {!exchangeRequestService.isDirectExchange(requests.values()) && (
              <div className="flex flex-row gap-x-2 items-center w-full">
                <Checkbox
                  id="urgent-checkbox"
                  checked={sendUrgentMessage}
                  onChange={(e) => {
                    const isChecked = e.target.checked
                    setSendUrgentMessage(isChecked)
                    if (!isChecked) form.setValue('urgentMessage', '')
                  }}
                />
                <label htmlFor="urgent-checkbox" className="text-sm cursor-pointer select-none">
                  O meu pedido é urgente por razões médicas ou outras
                </label>
              </div>
            )}
            {sendUrgentMessage && (
              <FormField
                control={form.control}
                name="urgentMessage"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <div className="flex flex-col gap-y-2">
                        <Textarea
                          {...field}
                          rows={4}
                          placeholder="Justifica a urgência do teu pedido de troca. Irá ser enviado diretamente para a comissão de inscrição de turmas."
                        />
                        <p className="text-xs font-bold text-left">
                          Se quiseres enviar ficheiros como comprovativo, podes enviar para a comissão de inscrição de
                          turmas.
                        </p>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <Button
              size="md"
              className="flex flex-row gap-x-2 bg-green-600 text-white hover:bg-green-700 w-full"
              type="submit"
              disabled={submittingRequest}
              // disabled={sendUrgentMessage ? false : conflictSeverity}
            >
              {submittingRequest ? (
                <p>A processar pedido...</p>
              ) : hasDuplicate ? (
                <>
                  <p>Submeter pedido e cancelar anterior</p>
                  <ArrowPathIcon className="h-5 w-5" />
                </>
              ) : (
                <>
                  <p>Submeter pedido</p>
                  <CheckBadgeIcon className="h-5 w-5" />
                </>
              )}
            </Button>
            {submittingRequest && <BeatLoader size={10} />}
          </form>
        </Form>
      )}
    </>
  )
}
